import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";

import { useCreateWorkBadgePdf } from "../hooks/useCreateWorkBadgePdf";
import { useSendWorkBadgeEmail } from "./useSendWorkBadgeEmail";
import { useUploadWorkBadgePdf } from "./useUploadWorkBadgePdf";

interface SendWorkBadgeEmailWithPdfParams {
  qualificationName: string;
  workerHeadshotBase64: string;
}

export function useSendWorkBadgeEmailWithPdf(
  options: UseMutationOptions<void, Error, SendWorkBadgeEmailWithPdfParams> = {}
) {
  const worker = useDefinedWorker();

  const { mutateAsync: createWorkBadgePdf } = useCreateWorkBadgePdf();
  const { mutateAsync: uploadWorkBadgePdf } = useUploadWorkBadgePdf();
  const { mutateAsync: sendWorkBadgeEmail } = useSendWorkBadgeEmail();

  return useMutation({
    mutationFn: async ({ qualificationName, workerHeadshotBase64 }) => {
      const { blob: pdfBlob } = await createWorkBadgePdf({
        qualificationName,
        workerHeadshotBase64,
        pageSize: "A4",
        badgeMarginInMm: 1.5,
      });

      await uploadWorkBadgePdf({
        workerId: worker.userId,
        qualification: qualificationName,
        pdfBlob,
      });

      await sendWorkBadgeEmail(
        {
          workerId: worker.userId,
          qualification: qualificationName,
        },
        {
          onSuccess: () => {
            logEvent(APP_V2_USER_EVENTS.WORK_BADGE_EMAIL_SENT_SUCCESS, {
              workerId: worker.userId,
              qualification: qualificationName,
            });
          },
        }
      );
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.WORK_BADGE_EMAIL_WITH_PDF_FAILURE,
    },
    ...options,
  });
}
